'use client'

/**
 * EVC-based Repay Dialog (uses Permit2 + EVC batch)
 *
 * Flow:
 * 1. User approves Permit2 to spend liability tokens (if not already approved)
 * 2. User signs Permit2 message
 * 3. Execute EVC batch: Permit2.permit() -> Vault.repay() -> (optional) Vault.disableController()
 *
 * Uses wagmi-evc hooks for EVC batch transactions.
 */

import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type Address, erc20Abi, formatUnits, maxUint256, parseUnits } from 'viem'
import { useAccount, useBalance, useConfig, useReadContract, useWriteContract } from 'wagmi'

import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { invalidateBalancesCache } from '@/domain/cache'
import { buildPermit2BatchRequest } from '@/domain/euler/evc/buildPermit2BatchRequest'
import { usePermit2Preflight } from '@/domain/euler/evc/usePermit2Preflight'
import { isWhypeAddress } from '@/domain/euler/nativeHype'
import { useAssetPrice } from '@/domain/euler/oracle/useAssetPrice'
import { useAvailableSubAccount } from '@/domain/euler/positions/useAvailableSubAccount'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { isTimeoutError, useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { useTrackedLendBorrowAction } from '@/features/analytics'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { DialogContentSkeleton } from '@/features/dialogs/common/components/skeletons/DialogContentSkeleton'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { isUserRejection } from '@/lib/errors'
import { assets } from '@/ui/assets'
import { getTokenImage } from '@/ui/assets/tokens'
import { Checkbox } from '@/ui/atoms/checkbox/Checkbox'
import { Slider } from '@/ui/atoms/slider/Slider'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { useDebounce } from '@/utils/useDebounce'
import {
  type Asset,
  type DisableControllerTransactionItem,
  type RepayTransactionItem,
  type RepayWithSharesTransactionItem,
  type TransactionItems,
  TransactionLabel,
  type WrapTransactionItem,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'
import BigNumber from 'bignumber.js'
import Image from 'next/image'
import { RepaySuccessView } from '../components/RepaySuccessView'
import { PARTIAL_REPAY_SAFETY_MULTIPLIER, formatRepayAmount, getPercentFromAmount } from '../logic/repayAmountUtils'

interface EvcRepayDialogContentProps {
  closeDialog: () => void
  liabilityVault: Address
  liabilitySymbol: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  maxRepayAmount: string // Total debt as string
  subAccountId?: number // Optional: if provided, use this sub-account instead of auto-discovering
  onSuccess?: () => void // Optional callback when transaction succeeds
}

// Safety multiplier applied to partial repays (< 100%) to prevent RepayTooMuch errors
// from interest accrual between UI calculation and transaction execution.
// Used in both calculateRepayAmount() and the initial percent calculation.

export function EvcRepayDialogContent({
  closeDialog,
  liabilityVault,
  liabilitySymbol,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  maxRepayAmount,
  subAccountId: propSubAccountId,
  onSuccess,
}: EvcRepayDialogContentProps) {
  const { address } = useAccount()
  const config = useConfig()
  const chainId = E_LEND_CHAIN_ID
  const queryClient = useQueryClient()
  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  // Check if this is a WHYPE vault (supports native HYPE repayment)
  const isWhypeVault = isWhypeAddress(liabilityAsset, chainId)

  // State
  const [repayPercent, setRepayPercent] = useState(100)
  const [sliderRepayPercent, setSliderRepayPercent] = useState(repayPercent)
  const [isSliderDragging, setIsSliderDragging] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [useNativeHype, setUseNativeHype] = useState(false) // Toggle for native HYPE payment
  const hasInitializedPercent = useRef(false) // Track if we've set initial percentage
  const userHasInteracted = useRef(false) // Track if user has manually set percentage
  const pendingRepayPercentRef = useRef<number | null>(null) // Pending value for debounced commit
  const pendingRepayAmountOnPaymentMethodChangeRef = useRef<number | null>(null)
  const [repayInputValue, setRepayInputValue] = useState('')
  const [isRepayInputFocused, setIsRepayInputFocused] = useState(false)

  // Debounce the slider dragging state to handle keyboard navigation
  // This prevents RPC spam when using arrow keys on the slider
  const { isDebouncing: isSliderDebouncing } = useDebounce(isSliderDragging, String(isSliderDragging), { delay: 300 })

  // Slider is active if currently dragging OR still in debounce period
  const isSliderActive = isSliderDragging || isSliderDebouncing

  // Sync slider with actual repayPercent when it changes externally
  useEffect(() => {
    setSliderRepayPercent(repayPercent)
  }, [repayPercent])

  // Find which sub-account has the debt (only if not explicitly provided)
  const {
    hasExistingPosition,
    existingSubAccountId,
    isLoading: isSubAccountLoading,
  } = useAvailableSubAccount({
    liabilityVault,
    chainId,
    enabled: !!address && propSubAccountId === undefined, // Only auto-discover if not provided
  })

  // Use provided subAccountId if available, otherwise use auto-discovered one
  const effectiveSubAccountId = propSubAccountId ?? existingSubAccountId
  const effectiveHasPosition = propSubAccountId !== undefined ? true : hasExistingPosition

  const subAccountAddress =
    effectiveHasPosition && effectiveSubAccountId !== null && address
      ? getSubAccountAddress(address, effectiveSubAccountId)
      : address

  // Fetch user's wallet balance (WHYPE tokens)
  const { data: walletBalanceRaw } = useReadContract({
    address: liabilityAsset,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId,
    query: {
      enabled: !!address && !!liabilityAsset,
      refetchInterval: 10_000,
    },
  })

  // Fetch native HYPE balance
  const { data: nativeBalance } = useBalance({
    address,
    chainId,
    query: {
      enabled: !!address && isWhypeVault,
      refetchInterval: 10_000,
    },
  })

  // Use native HYPE balance or WHYPE balance based on toggle
  const activeWalletBalanceRaw = useNativeHype ? nativeBalance?.value : walletBalanceRaw
  const activeSymbol = useNativeHype ? 'HYPE' : liabilitySymbol

  const maxRepayAmountNum = Number(maxRepayAmount)
  // Reserve 0.001 HYPE for gas costs when using native HYPE
  const GAS_RESERVE = 0.001
  const rawWalletBalance =
    activeWalletBalanceRaw !== undefined ? Number(formatUnits(activeWalletBalanceRaw, liabilityDecimals)) : 0
  const walletBalance = useNativeHype ? Math.max(0, rawWalletBalance - GAS_RESERVE) : rawWalletBalance
  const effectiveMaxRepay =
    activeWalletBalanceRaw !== undefined ? Math.min(maxRepayAmountNum, walletBalance) : maxRepayAmountNum
  const maxSliderPercent = useMemo(() => {
    if (maxRepayAmountNum <= 0) return 0
    if (effectiveMaxRepay >= maxRepayAmountNum) return 100
    return getPercentFromAmount({ amount: effectiveMaxRepay, maxRepayAmount: maxRepayAmountNum })
  }, [effectiveMaxRepay, maxRepayAmountNum])

  const clampRepayPercent = useCallback(
    (percent: number) => Math.min(Math.max(0, percent), maxSliderPercent),
    [maxSliderPercent],
  )
  const sliderStep = useMemo(() => {
    if (maxSliderPercent <= 0) return 1
    if (maxSliderPercent >= 1) return 1
    return maxSliderPercent / 100
  }, [maxSliderPercent])
  const formatSliderPercent = useCallback(
    (percent: number) =>
      percent.toLocaleString(undefined, { maximumFractionDigits: percent < 10 && percent % 1 !== 0 ? 2 : 0 }),
    [],
  )

  // Commit pending value when slider activity ends (handles both pointer and keyboard)
  useEffect(() => {
    if (!isSliderActive && pendingRepayPercentRef.current !== null) {
      setRepayPercent(Math.min(Math.max(0, pendingRepayPercentRef.current), maxSliderPercent))
      userHasInteracted.current = true
      pendingRepayPercentRef.current = null
    }
  }, [isSliderActive, maxSliderPercent])

  // Reset initialization flag when payment method changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    hasInitializedPercent.current = false
    userHasInteracted.current = false // Also reset interaction tracking
  }, [useNativeHype])

  useEffect(() => {
    if (pendingRepayAmountOnPaymentMethodChangeRef.current === null || activeWalletBalanceRaw === undefined) return

    const nextAmount = Math.min(pendingRepayAmountOnPaymentMethodChangeRef.current, effectiveMaxRepay)
    const nextPercent = getPercentFromAmount({ amount: nextAmount, maxRepayAmount: maxRepayAmountNum })

    setRepayPercent(clampRepayPercent(nextPercent))
    userHasInteracted.current = true
    hasInitializedPercent.current = true
    pendingRepayAmountOnPaymentMethodChangeRef.current = null
  }, [activeWalletBalanceRaw, clampRepayPercent, effectiveMaxRepay, maxRepayAmountNum])

  // Set initial repay percentage based on wallet balance
  // If user can't afford 100%, default to a percentage they can afford
  useEffect(() => {
    // Don't recalculate if user has manually interacted with the slider
    if (userHasInteracted.current) return

    if (!hasInitializedPercent.current && walletBalance > 0 && maxRepayAmountNum > 0) {
      if (walletBalance >= maxRepayAmountNum) {
        // User can afford 100% — full repay with MaxUint256
        setRepayPercent(100)
      } else {
        // Keep the slider aligned with the actual repayable share of debt.
        setRepayPercent(maxSliderPercent)
      }
      hasInitializedPercent.current = true
    }
  }, [walletBalance, maxRepayAmountNum, maxSliderPercent])

  useEffect(() => {
    if (repayPercent > maxSliderPercent) {
      setRepayPercent(maxSliderPercent)
    }
  }, [maxSliderPercent, repayPercent])

  const calculateRepayAmount = useCallback(
    (percent: number) => {
      // Calculate the raw amount
      let calculatedAmount = new BigNumber(maxRepayAmountNum).multipliedBy(percent).dividedBy(100)

      // For partial repayments (< 100%), apply a safety buffer to prevent RepayTooMuch errors
      // This accounts for interest accrual between UI calculation and transaction execution
      if (percent < 100 && percent > 0) {
        // Reduce by 0.5% to ensure we're safely below the accruing debt
        calculatedAmount = calculatedAmount.multipliedBy(PARTIAL_REPAY_SAFETY_MULTIPLIER)
      }

      // Defensive cap for any future 100% path that resolves above wallet balance.
      if (percent === 100 && walletBalance > 0 && walletBalance < maxRepayAmountNum) {
        calculatedAmount = BigNumber.min(
          calculatedAmount,
          new BigNumber(walletBalance).multipliedBy(PARTIAL_REPAY_SAFETY_MULTIPLIER),
        )
      }

      // Round down to token precision to avoid exceeding debt due to rounding
      return calculatedAmount.decimalPlaces(liabilityDecimals, BigNumber.ROUND_DOWN).toNumber()
    },
    [maxRepayAmountNum, walletBalance, liabilityDecimals],
  )

  // Calculate repay amount based on percent
  const repayAmount = useMemo(() => {
    return formatRepayAmount(calculateRepayAmount(repayPercent))
  }, [calculateRepayAmount, repayPercent])

  const canRepayFullDebt = useMemo(() => {
    // No buffer required here — the downstream logic handles interest accrual:
    //
    //   isFullRepay=true
    //     → RepayTransactionItem.isMax=true (line ~430)
    //     → VaultTransactionFactory converts to MaxUint256 (vault pulls exact debt)
    //     → Permit2/wrap amount = min(debt + buffer, walletBalance) (line ~305-320)
    //       - ERC-20: 1% buffer via repayAmountInBaseUnits
    //       - HYPE: 0.5% buffer via repayAmountInBaseUnits, capped at walletBalance
    //
    // If debt grows past walletBalance before execution, the tx cleanly reverts.
    // This is preferable to the old 1% buffer gate which forced isFullRepay=false,
    // used a calculated amount instead of MaxUint256, and left dust.
    return walletBalance >= maxRepayAmountNum
  }, [maxRepayAmountNum, walletBalance])

  const isFullRepay = repayPercent === 100 && canRepayFullDebt
  const isAttemptingFullDebtRepay = repayPercent === 100 && maxSliderPercent === 100

  // Warn when balance barely covers the debt — transaction may revert if interest accrues
  const bufferMultiplier = useNativeHype ? 1.005 : 1.01
  const isCloseToLimit =
    activeWalletBalanceRaw !== undefined &&
    isAttemptingFullDebtRepay &&
    walletBalance < maxRepayAmountNum * bufferMultiplier

  const repayAmountRaw = parseUnits(repayAmount || '0', liabilityDecimals)
  // When wallet balance is known and zero (or below gas reserve), surface a clear
  // warning immediately instead of showing a dead 0%/0 form with no explanation.
  const hasZeroBalance = activeWalletBalanceRaw !== undefined && walletBalance <= 0
  const insufficientBalance =
    hasZeroBalance || (activeWalletBalanceRaw !== undefined && repayAmountRaw > activeWalletBalanceRaw)
  const canRepay =
    (propSubAccountId !== undefined || !isSubAccountLoading) && // Only wait for loading if auto-discovering
    activeWalletBalanceRaw !== undefined &&
    Number(repayAmount) > 0 &&
    !insufficientBalance &&
    !!subAccountAddress

  // Create liability token for Permit2
  const liabilityToken = useMemo(
    () =>
      new Token({
        address: CheckedAddress(liabilityAsset),
        symbol: TokenSymbol(liabilitySymbol),
        name: liabilitySymbol,
        decimals: liabilityDecimals,
        unitPriceUsd: '0',
        logoURI: liabilityLogoURI,
      }),
    [liabilityAsset, liabilitySymbol, liabilityDecimals, liabilityLogoURI],
  )

  // Calculate the EXACT amount in base units to use for both Permit2 AND the transaction.
  // This ensures the signed permit amount matches the transaction amount exactly (no rounding mismatches).
  //
  // For full repay (isFullRepay=true):
  //   The vault is called with MaxUint256, so it pulls exactly the current debt.
  //   We add a buffer to the Permit2/wrap amount to cover interest accrual between
  //   UI load and execution, then cap at walletBalance (can't spend more than we have).
  //   If debt grows past walletBalance before execution, the tx reverts — correct behavior.
  const repayAmountInBaseUnits = useMemo(() => {
    if (isFullRepay) {
      // For 100% repayment with native HYPE: add 0.5% buffer to cover interest accrual + skim rounding loss
      // Cap at walletBalance to avoid wrapping more HYPE than available (after gas reserve)
      if (useNativeHype) {
        const bufferedAmount = new BigNumber(maxRepayAmount).multipliedBy(1.005)
        const finalAmount = BigNumber.min(bufferedAmount, walletBalance)
        return parseUnits(finalAmount.toFixed(liabilityDecimals), liabilityDecimals)
      }
      // For 100% repayment with ERC-20 (Permit2): add 1% buffer to cover interest accrual
      // between simulation and execution. Also add minimum absolute buffer for dust amounts
      // where percentage-based buffers are meaningless (e.g., 1% of 490 wei = 4.9 wei)
      const percentageBuffer = new BigNumber(maxRepayAmount).multipliedBy(1.01)
      // Minimum buffer of 1000 base units (in human-readable format) to handle dust amounts
      // maxRepayAmount is in human-readable format, so we convert 1000 base units to the same scale
      const minBufferInHumanReadable = new BigNumber(1000).shiftedBy(-liabilityDecimals)
      const minAbsoluteBuffer = new BigNumber(maxRepayAmount).plus(minBufferInHumanReadable)
      const bufferedAmount = BigNumber.max(percentageBuffer, minAbsoluteBuffer)
      const finalAmount = BigNumber.min(bufferedAmount, walletBalance)
      return parseUnits(finalAmount.toFixed(liabilityDecimals), liabilityDecimals)
    }
    // For partial repayment, use the calculated repayAmount
    return parseUnits(repayAmount || '0', liabilityDecimals)
  }, [isFullRepay, repayAmount, maxRepayAmount, walletBalance, liabilityDecimals, useNativeHype])

  // Permit2 preflight check - uses the EXACT base unit amount
  // Only needed for ERC-20 token repayment, not native HYPE
  // Disable while slider is dragging to prevent spamming RPC
  const permit2 = usePermit2Preflight({
    token: liabilityToken,
    spender: liabilityVault,
    amount: repayAmountInBaseUnits,
    enabled: !useNativeHype && canRepay && !!address && !isSliderActive,
  })

  // Build RepayTransactionItem or RepayWithSharesTransactionItem (+ optionally DisableController)
  const transactions = useMemo((): TransactionItems => {
    if (!address || !subAccountAddress || Number(repayAmount) <= 0) return []

    const asset: Asset = {
      address: liabilityAsset,
      decimals: liabilityDecimals,
      name: liabilitySymbol,
      symbol: liabilitySymbol,
    }

    const txs: TransactionItems = []

    // For native HYPE: use RepayWithSharesTransactionItem with nested WrapTransactionItem
    if (useNativeHype) {
      // WrapTransactionItem wraps native HYPE → WHYPE
      // The asset must be the WRAPPED token (WHYPE) for WETHTransactionFactory to recognize it
      const wrapTx: WrapTransactionItem = {
        label: TransactionLabel.WRAP,
        from: address,
        to: subAccountAddress, // Transfer to subaccount for repayment
        asset, // WHYPE (wrapped asset)
        vault: liabilityVault,
        value: repayAmountInBaseUnits.toString(),
      }

      // RepayWithSharesTransactionItem for native HYPE
      // The factory will handle wrap → transfer → skim → repayWithShares
      // Always use maxUint256 and isMax: true to consume all shares from skim (handles rounding)
      // IMPORTANT: from/to are swapped because VaultTransactionFactory uses 'from' for onBehalfOfAccount
      // We need onBehalfOfAccount to be the subaccount (where the debt is)
      const repayWithSharesTx: RepayWithSharesTransactionItem = {
        label: TransactionLabel.REPAYWITHSHARES,
        from: subAccountAddress, // This becomes onBehalfOfAccount (the account with debt)
        to: subAccountAddress, // This is the receiver in repayWithShares args
        asset, // WHYPE (liability asset)
        vault: liabilityVault,
        value: maxUint256.toString(),
        isMax: true, // Always true to consume all available shares from skim
        transactions: [wrapTx], // Nested wrap transaction
      }

      txs.push(repayWithSharesTx)

      // Disable controller on full repayment - collateral stays in sub-account as savings
      if (isFullRepay && effectiveHasPosition && effectiveSubAccountId !== null) {
        const disableControllerTx: DisableControllerTransactionItem = {
          label: TransactionLabel.DISABLE_CONTROLLER,
          from: subAccountAddress,
          to: subAccountAddress,
          asset,
          vault: liabilityVault,
        }
        txs.push(disableControllerTx)
      }
    } else {
      // For ERC-20 (WHYPE): use standard RepayTransactionItem with Permit2
      const repayTx: RepayTransactionItem = {
        label: TransactionLabel.REPAY,
        from: address,
        to: subAccountAddress,
        asset,
        vault: liabilityVault,
        value: repayAmountInBaseUnits.toString(),
        isMax: isFullRepay, // Use maxUint256 for ERC-20 full repayment
      }
      txs.push(repayTx)

      // Disable controller on full repayment - collateral stays in sub-account as savings
      if (isFullRepay && effectiveHasPosition && effectiveSubAccountId !== null) {
        const disableControllerTx: DisableControllerTransactionItem = {
          label: TransactionLabel.DISABLE_CONTROLLER,
          from: subAccountAddress,
          to: subAccountAddress,
          asset,
          vault: liabilityVault,
        }
        txs.push(disableControllerTx)
      }
    }

    return txs
  }, [
    address,
    subAccountAddress,
    liabilityAsset,
    liabilityDecimals,
    liabilitySymbol,
    liabilityVault,
    repayAmount,
    repayAmountInBaseUnits,
    isFullRepay,
    effectiveHasPosition,
    effectiveSubAccountId,
    useNativeHype,
  ])

  // Simulate the EVC batch
  // Pass config explicitly to avoid wagmi context mismatch in production builds
  // Disable while slider is dragging to prevent spamming RPC
  const {
    data: simulationData,
    isLoading: isSimulating,
    error: simulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    // Include msg.value for native HYPE wrapping to match execution
    value: useNativeHype ? repayAmountInBaseUnits : undefined,
    query: {
      enabled:
        canRepay &&
        transactions.length > 0 &&
        !!address &&
        (useNativeHype || permit2.status === 'ready') &&
        !isSliderActive,
      retry: false,
    },
  })

  // Write contract
  const { writeContract, isPending: isWriting, error: writeError, data: txHash } = useWriteContract()

  // Wait for transaction
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })
  const { markSubmitted } = useTrackedLendBorrowAction({
    actionType: 'repay',
    token: liabilitySymbol,
    market: liabilityVault,
    marketType: 'eborrow',
    amount: repayAmount,
    txHash,
    isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  // Execute the batch
  const handleExecute = useCallback(async () => {
    if (!simulationData?.request) {
      console.error('[EvcRepayDialogContent] Simulation not ready')
      return
    }

    try {
      if (useNativeHype) {
        markSubmitted()
        writeContract({ ...simulationData.request, value: repayAmountInBaseUnits } as any)
        return
      }

      const executionRequest = buildPermit2BatchRequest({
        simulationData,
        permitTx: permit2.status === 'ready' ? permit2.permitTx : null,
        chainId,
      })
      markSubmitted()
      writeContract(executionRequest as any)
    } catch (err) {
      console.error('[EvcRepayDialogContent] Execute error:', err)
    }
  }, [
    simulationData,
    writeContract,
    permit2.permitTx,
    permit2.status,
    chainId,
    useNativeHype,
    repayAmountInBaseUnits,
    markSubmitted,
  ])

  // ========== ACTION TIMEOUT TRACKING ==========
  // Track timeout for repay execution to provide feedback when operations hang
  const repayTimeout = useActionTimeout({
    timeoutMs: 15000, // 15 seconds for repay operations
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  // Wrapped execute handler that tracks timeout
  const handleExecuteWithTimeout = useCallback(() => {
    repayTimeout.handleActionClick(handleExecute)
  }, [repayTimeout, handleExecute])

  // Determine action handler states for the grid (must be before conditional returns)
  const approveStatus: ActionHandlerState['status'] = useMemo(() => {
    if (useNativeHype) return 'success' // Skip for native HYPE
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval') return 'ready'
    if (permit2.status === 'approving') return 'loading' // Loading allowance or approving
    if (permit2.isApproving) return 'loading' // Active approval transaction
    return 'success'
  }, [permit2.status, permit2.isApproving, useNativeHype])

  const signStatus: ActionHandlerState['status'] = useMemo(() => {
    if (useNativeHype) return 'success' // Skip for native HYPE
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval' || permit2.status === 'approving' || permit2.isApproving) return 'disabled'
    if (permit2.status === 'needs_signature') return 'ready'
    if (permit2.status === 'signing') return 'loading' // Loading signature
    if (permit2.isSigning) return 'loading' // Active signing
    return 'success'
  }, [permit2.status, permit2.isApproving, permit2.isSigning, useNativeHype])

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    // Active transaction states take priority - never allow retry while tx is pending
    if (isWriting || isWaiting) return 'loading'

    // Check prerequisites - must be met even after timeout
    if (!canRepay) return 'disabled'

    if (useNativeHype) {
      // For native HYPE: need simulation ready (no Permit2 needed)
      if (!simulationData) {
        return isSimulating ? 'loading' : 'disabled'
      }
      return 'ready'
    }

    // For ERC-20: need Permit2 ready + simulation
    if (permit2.status !== 'ready') return 'disabled'
    if (!simulationData) {
      return isSimulating ? 'loading' : 'disabled'
    }
    return 'ready'
  }, [permit2.status, simulationData, canRepay, isSimulating, isWriting, isWaiting, useNativeHype])

  // Mark as success when confirmed
  useEffect(() => {
    if (isConfirmed && !isSuccess) {
      setIsSuccess(true)
      onSuccess?.() // Notify parent that transaction succeeded
      // Invalidate subaccount cache to trigger discovery refresh
      // This ensures freed subaccounts are detected for reuse
      invalidateSubaccounts()
    }
  }, [isConfirmed, isSuccess, onSuccess, invalidateSubaccounts])

  // Invalidate queries after successful transaction to refresh UI
  useEffect(() => {
    if (isConfirmed && address) {
      invalidateBalancesCache({
        queryClient,
        chainId,
        address,
        vaultAddress: liabilityVault,
        subAccountAddress,
      })
    }
  }, [isConfirmed, address, subAccountAddress, chainId, queryClient, liabilityVault])

  const liabilityImage = getTokenImage(TokenSymbol(liabilitySymbol)) || liabilityLogoURI

  // Compute display values - use slider value during drag for immediate visual feedback
  const displayRepayAmount = useMemo(() => {
    if (!isSliderActive) return Number(repayAmount)
    return calculateRepayAmount(sliderRepayPercent)
  }, [isSliderActive, repayAmount, calculateRepayAmount, sliderRepayPercent])
  const displayRemainingDebt = Math.max(0, maxRepayAmountNum - displayRepayAmount)
  const cappedRepayAmount = useMemo(
    () => calculateRepayAmount(maxSliderPercent),
    [calculateRepayAmount, maxSliderPercent],
  )
  const { priceUSD: liabilityPriceUSD } = useAssetPrice({
    assetAddress: liabilityAsset,
    vaultAddress: liabilityVault,
  })
  const repayValueUSD = displayRepayAmount * liabilityPriceUSD

  useEffect(() => {
    if (!isRepayInputFocused) {
      setRepayInputValue(formatRepayAmount(displayRepayAmount))
    }
  }, [displayRepayAmount, isRepayInputFocused])

  const handleRepayInputChange = (value: string) => {
    pendingRepayPercentRef.current = null
    setRepayInputValue(value)
    const numericValue = Math.min(Number(value) || 0, effectiveMaxRepay)
    const percent = getPercentFromAmount({ amount: numericValue, maxRepayAmount: maxRepayAmountNum })
    setRepayPercent(clampRepayPercent(percent))
    userHasInteracted.current = true
  }

  const handleSetMax = () => {
    setRepayPercent(maxSliderPercent)
    setRepayInputValue(formatRepayAmount(cappedRepayAmount))
    userHasInteracted.current = true
  }

  const handlePaymentMethodChange = (checked: boolean) => {
    pendingRepayAmountOnPaymentMethodChangeRef.current = displayRepayAmount > 0 ? displayRepayAmount : null
    setUseNativeHype(checked)
  }

  // Only show errors that aren't user rejections
  const displayError = useMemo(() => {
    // Timeout error takes priority for better UX feedback
    if (repayTimeout.isTimedOut) {
      return repayTimeout.displayError
    }
    const errors = [permit2.error, simulationError, writeError, waitError].filter(Boolean)
    return errors.find((err) => !isUserRejection(err)) || null
  }, [permit2.error, simulationError, writeError, waitError, repayTimeout.isTimedOut, repayTimeout.displayError])

  // Log errors for debugging (but not user rejections)
  useEffect(() => {
    if (simulationError && !isUserRejection(simulationError)) {
      console.error('[EvcRepayDialogContent] Simulation error:', simulationError)
    }
    if (writeError && !isUserRejection(writeError)) {
      console.error('[EvcRepayDialogContent] Write error:', writeError)
    }
    if (waitError && !isUserRejection(waitError)) {
      console.error('[EvcRepayDialogContent] Wait error:', waitError)
    }
  }, [simulationError, writeError, waitError])

  // Show loading state while determining sub-account (only if auto-discovering)
  if (propSubAccountId === undefined && isSubAccountLoading) {
    return <DialogContentSkeleton />
  }

  // Success state
  if (isSuccess) {
    // Create the display token for the repay amount
    const repayToken = new Token({
      symbol: TokenSymbol(liabilitySymbol),
      name: liabilitySymbol,
      decimals: liabilityDecimals,
      address: CheckedAddress(liabilityAsset),
      unitPriceUsd: liabilityPriceUSD > 0 ? String(liabilityPriceUSD) : '0',
      ...(liabilityLogoURI && { logoURI: liabilityLogoURI }),
    })

    return (
      <RepaySuccessView
        token={repayToken}
        amount={NormalizedUnitNumber(repayAmount)}
        onProceed={closeDialog}
        proceedText="Close"
      />
    )
  }

  return (
    <MultiPanelDialog>
      <DialogPanel>
        {/* Repay Amount Selector */}
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-muted-foreground text-sm">Repay Amount</span>
              <span className="text-muted-foreground text-xs">
                Slider max: {formatSliderPercent(maxSliderPercent)}% of debt
              </span>
            </div>
            <span className="font-medium text-lg text-white">{formatSliderPercent(sliderRepayPercent)}%</span>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Amount to repay</span>
              <button
                type="button"
                onClick={handleSetMax}
                disabled={effectiveMaxRepay <= 0}
                className="text-primary text-xs transition-colors disabled:cursor-default disabled:text-muted-foreground hover:text-primary/80"
              >
                Max
              </button>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-3">
                {liabilityImage && (
                  <Image src={liabilityImage} alt={liabilitySymbol} width={32} height={32} className="rounded-full" />
                )}
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Debt Repaid</span>
                  <span className="font-medium text-sm text-white">{activeSymbol}</span>
                </div>
              </div>
              <div className="flex min-w-0 flex-col items-end gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  value={repayInputValue}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized === null) return
                    handleRepayInputChange(normalized)
                  }}
                  onFocus={() => setIsRepayInputFocused(true)}
                  onBlur={() => {
                    setIsRepayInputFocused(false)
                    setRepayInputValue(formatRepayAmount(displayRepayAmount))
                  }}
                  className="min-w-0 bg-transparent text-right font-semibold text-lg text-primary outline-none placeholder:text-primary/40"
                  placeholder="0.00"
                />
                {repayValueUSD > 0 && (
                  <span className="text-muted-foreground text-xs">
                    ${repayValueUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div>
            <Slider
              value={[sliderRepayPercent]}
              onValueChange={([value]) => {
                setIsSliderDragging(true)
                const nextValue = clampRepayPercent(value ?? maxSliderPercent)
                setSliderRepayPercent(nextValue)
                pendingRepayPercentRef.current = nextValue
              }}
              onValueCommit={([value]) => {
                // Store pending value - actual commit happens via effect when slider activity ends
                pendingRepayPercentRef.current = clampRepayPercent(value ?? maxSliderPercent)
              }}
              onPointerDown={() => setIsSliderDragging(true)}
              onPointerUp={() => setIsSliderDragging(false)}
              onLostPointerCapture={() => setIsSliderDragging(false)}
              onKeyUp={() => setIsSliderDragging(false)}
              onBlur={() => setIsSliderDragging(false)}
              max={maxSliderPercent}
              step={sliderStep}
            />
          </div>
          <div className="flex justify-between text-muted-foreground text-xs">
            <span>0%</span>
            <span>{formatSliderPercent(maxSliderPercent * 0.25)}%</span>
            <span>{formatSliderPercent(maxSliderPercent * 0.5)}%</span>
            <span>{formatSliderPercent(maxSliderPercent * 0.75)}%</span>
            <span>{formatSliderPercent(maxSliderPercent)}%</span>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          <TransactionOverviewDetailsItem label="Current Debt">
            {maxRepayAmountNum.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}{' '}
            {liabilitySymbol}
          </TransactionOverviewDetailsItem>
          <TransactionOverviewDetailsItem label="Remaining Debt">
            {displayRemainingDebt.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}{' '}
            {liabilitySymbol}
          </TransactionOverviewDetailsItem>
          <TransactionOverviewDetailsItem label="Wallet Balance">
            {walletBalance.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}{' '}
            {activeSymbol}
          </TransactionOverviewDetailsItem>

          {/* Native HYPE Payment Option (only show for WHYPE vaults) */}
          {isWhypeVault && (
            <TransactionOverviewDetailsItem label="Pay with native HYPE">
              <Checkbox
                checked={useNativeHype}
                onCheckedChange={(checked) => handlePaymentMethodChange(checked === true)}
              />
            </TransactionOverviewDetailsItem>
          )}
        </div>

        {/* Warnings */}
        {insufficientBalance && (
          <div className="mt-4 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
            <p className="text-sm text-yellow-400">
              {hasZeroBalance
                ? `You have no ${activeSymbol} in your wallet to repay this debt.`
                : 'Insufficient balance to cover debt plus accrued interest.'}
            </p>
          </div>
        )}

        {/* Warning: balance barely covers debt, tx may revert if interest accrues */}
        {!insufficientBalance && isCloseToLimit && (
          <div className="mt-4 max-w-sm rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
            <p className="text-sm text-yellow-400">
              Your balance barely covers the debt. If interest accrues before the transaction executes, it may need to
              be retried.
            </p>
          </div>
        )}

        {/* Timeout error display - shows when operation takes too long */}
        <TimeoutErrorDisplay
          isTimedOut={repayTimeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
        />

        {/* Error display */}
        {displayError && !isTimeoutError(displayError) && (
          <TransactionErrorPanel
            error={displayError}
            showSimulationWarning={!!simulationError && !isUserRejection(simulationError)}
          />
        )}

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid (matching old design) */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          {/* Step 1: Approve Permit2 */}
          <ActionRow index={1}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
            <ActionRow.Title icon={<TokenIcon token={liabilityToken} className="h-6" />} actionStatus={approveStatus}>
              Approve Permit2
            </ActionRow.Title>
            <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
            <ActionRow.Action
              onAction={() => permit2.approvePermit2?.()}
              status={isSliderActive ? 'disabled' : approveStatus}
            >
              Approve
            </ActionRow.Action>
          </ActionRow>

          {/* Step 2: Sign Permit */}
          <ActionRow index={2}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
            <ActionRow.Title icon={<TokenIcon token={liabilityToken} className="h-6" />} actionStatus={signStatus}>
              Sign Permit
            </ActionRow.Title>
            <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
            <ActionRow.Action
              onAction={() => permit2.signPermit2?.()}
              status={isSliderActive ? 'disabled' : signStatus}
            >
              Sign
            </ActionRow.Action>
          </ActionRow>

          {/* Step 3: Repay */}
          <ActionRow index={3}>
            <ActionRow.Icon path={assets.actions.repay} actionStatus={executeStatus} />
            <ActionRow.Title icon={<TokenIcon token={liabilityToken} className="h-6" />} actionStatus={executeStatus}>
              Repay
            </ActionRow.Title>
            <ActionRow.Description successMessage="Repaid!" actionStatus={executeStatus} variant="compact" />
            <ActionRow.Action onAction={handleExecuteWithTimeout} status={isSliderActive ? 'disabled' : executeStatus}>
              {repayTimeout.isTimedOut ? 'Retry' : isSimulating ? 'Preparing...' : 'Repay'}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
